import { useState, useMemo, useEffect } from "react";
import { usePageHeader } from "@/hooks/usePageHeader";
import {
  Loader2,
  Plus,
  Signal,
  Eye,
  EyeOff,
  Trash2,
  Link,
  Radio,
  Wifi,
  WifiOff,
  Battery,
  MapPin,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useCoverageDevices,
  useExternalSessions,
  useMultipleSessionsMapData,
  useDeleteExternalSession,
  useCoverageApiHealth,
  useLinkCoverageSession,
  type ExternalSession,
  type Device,
} from "@/hooks/useNetworkCoverage";
import { useProjects } from "@/hooks/useProjects";
import { CoverageMapViewer } from "@/components/project-detail/network-coverage/CoverageMapViewer";
import { NewSessionDialog } from "@/components/network-coverage/NewSessionDialog";
import { formatDistanceStrict } from "date-fns";
import { formatProjectName } from "@/lib/format-project-name";

export default function NetworkCoveragePage() {
  const [isNewSessionOpen, setIsNewSessionOpen] = useState(false);
  const [visibleSessionIds, setVisibleSessionIds] = useState<Set<string>>(
    new Set(),
  );
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [linkingSession, setLinkingSession] = useState<ExternalSession | null>(
    null,
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  const { data: healthData, isLoading: isLoadingHealth } =
    useCoverageApiHealth();
  const { data: devices = [], isLoading: isLoadingDevices } =
    useCoverageDevices();
  const { data: sessions = [], isLoading: isLoadingSessions } =
    useExternalSessions();
  const { data: projects = [] } = useProjects();
  const deleteSession = useDeleteExternalSession();
  const linkSession = useLinkCoverageSession();

  // Fetch map data for visible sessions
  const visibleIds = useMemo(
    () => Array.from(visibleSessionIds),
    [visibleSessionIds],
  );
  const { data: mapDataArray = [], isLoading: isLoadingMap } =
    useMultipleSessionsMapData(visibleIds);

  // Default: hide all sessions on load (unlike Plan tab which shows all)
  // This is intentional for the standalone page

  const toggleVisibility = (sessionId: string) => {
    setVisibleSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const showAll = () => {
    setVisibleSessionIds(new Set(sessions.map((s) => s.id)));
  };

  const hideAll = () => {
    setVisibleSessionIds(new Set());
  };

  const handleDelete = async () => {
    if (deleteSessionId) {
      await deleteSession.mutateAsync(deleteSessionId);
      setVisibleSessionIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteSessionId);
        return next;
      });
      setDeleteSessionId(null);
    }
  };

  const handleLink = async () => {
    if (linkingSession && selectedProjectId) {
      await linkSession.mutateAsync({
        projectId: selectedProjectId,
        externalSession: linkingSession,
      });
      setLinkingSession(null);
      setSelectedProjectId("");
    }
  };

  // Transform data for map viewer
  const sessionsForMap = useMemo(() => {
    return sessions.map((session) => {
      const mapData = mapDataArray.find((d) => d.id === session.id);
      return {
        id: session.id,
        points: mapData?.points || [],
        visible: visibleSessionIds.has(session.id),
        deviceName: session.device_name,
      };
    });
  }, [sessions, mapDataArray, visibleSessionIds]);

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatBitrate = (kbps: number) => {
    if (kbps >= 1000) {
      return `${(kbps / 1000).toFixed(1)} Mbps`;
    }
    return `${kbps} kbps`;
  };

  const getDuration = (session: ExternalSession) => {
    if (!session.ended_at) return "In progress";
    return formatDistanceStrict(
      new Date(session.ended_at),
      new Date(session.started_at),
    );
  };

  const apiConnected = healthData?.status === "ok";
  const liveUConnected = healthData?.liveUConnected;

  usePageHeader({ title: "Network Coverage" });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button onClick={() => setIsNewSessionOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Session
        </Button>
      </div>

      {/* API Status */}
      {!apiConnected && !isLoadingHealth && (
        <Card className="border-destructive">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="font-medium">Coverage API Unavailable</p>
              <p className="text-sm text-muted-foreground">
                Make sure the network-coverage-tester service is running on the
                configured URL.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Devices Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            LiveU Devices
          </CardTitle>
          <CardDescription>
            Connected devices and their current status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingDevices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {apiConnected ? "No devices found" : "Unable to fetch devices"}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {devices.map((device) => (
                <DeviceCard key={device.uid} device={device} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Coverage Map</CardTitle>
              <CardDescription>
                Toggle session visibility to display tracks on the map
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={showAll}>
                <Eye className="h-4 w-4 mr-2" />
                Show All
              </Button>
              <Button variant="outline" size="sm" onClick={hideAll}>
                <EyeOff className="h-4 w-4 mr-2" />
                Hide All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CoverageMapViewer
            sessions={sessionsForMap}
            isLoading={isLoadingMap}
            className="h-[500px]"
          />
        </CardContent>
      </Card>

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sessions</CardTitle>
          <CardDescription>All coverage recording sessions</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingSessions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Signal className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No sessions yet</p>
              <p className="text-sm">
                Create a new session from device log data
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Visible</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>GPX</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <Button
                        variant={
                          visibleSessionIds.has(session.id)
                            ? "default"
                            : "outline"
                        }
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => toggleVisibility(session.id)}
                      >
                        {visibleSessionIds.has(session.id) ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">
                      {session.device_name || session.device_uid}
                    </TableCell>
                    <TableCell>{formatDateTime(session.started_at)}</TableCell>
                    <TableCell>
                      {session.ended_at
                        ? formatDateTime(session.ended_at)
                        : "—"}
                    </TableCell>
                    <TableCell>{getDuration(session)}</TableCell>
                    <TableCell>
                      {session.gpx_uploaded === 1 ? (
                        <Badge variant="secondary">
                          <MapPin className="h-3 w-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setLinkingSession(session)}
                        >
                          <Link className="h-4 w-4 mr-1" />
                          Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteSessionId(session.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Session Dialog */}
      <NewSessionDialog
        open={isNewSessionOpen}
        onOpenChange={setIsNewSessionOpen}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteSessionId}
        onOpenChange={() => setDeleteSessionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this coverage session and all its
              data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link to Project Dialog */}
      <AlertDialog
        open={!!linkingSession}
        onOpenChange={() => {
          setLinkingSession(null);
          setSelectedProjectId("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Link to Project</AlertDialogTitle>
            <AlertDialogDescription>
              Select a project to link this coverage session to.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a project..." />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {formatProjectName(project.name, project.event_start_date)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLink}
              disabled={!selectedProjectId || linkSession.isPending}
            >
              {linkSession.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Device Card Component
function DeviceCard({ device }: { device: Device }) {
  const isOnline =
    device.status?.state === "streaming" || device.status?.state === "ready";
  const bitrate = device.status?.bitrate || 0;
  const battery = device.status?.battery?.percentage;

  const formatBitrate = (kbps: number) => {
    if (kbps >= 1000) {
      return `${(kbps / 1000).toFixed(1)} Mbps`;
    }
    return `${kbps} kbps`;
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {isOnline ? (
                <Wifi className="h-4 w-4 text-green-500" />
              ) : (
                <WifiOff className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="font-medium truncate">{device.name}</span>
            </div>
            <p className="text-xs text-muted-foreground truncate mb-2">
              {device.uid}
            </p>
            <div className="flex items-center gap-3 text-sm">
              {bitrate > 0 && (
                <span className="text-green-600">{formatBitrate(bitrate)}</span>
              )}
              {battery !== undefined && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Battery className="h-3 w-3" />
                  {battery}%
                </span>
              )}
            </div>
          </div>
          <Badge variant={isOnline ? "default" : "secondary"}>
            {isOnline ? "Online" : "Offline"}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
